import { TrashIcon, UsersIcon, PlusIcon } from '@heroicons/react/24/solid';
import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { trainingSessionsAPI } from '../../services/api';

const CreateSessionModal = ({
  show,
  handleClose,
  handleSubmit,
  editingSession,
  formData,
  setFormData,
  crews,
  coaches = [],
  createSessionMutation,
  updateSessionMutation,
  calculateEndTime,
  formatCrewNames,
  updateCrewAssignment,
  removeCrewAssignment,
  addCrewAssignment,
}) => {
  const [validationErrors, setValidationErrors] = useState([]);
  const [showTrainingGroups, setShowTrainingGroups] = useState(true);

  // Fetch user's training groups and their crews
  const { data: userTrainingGroups = [], isLoading: loadingTrainingGroups } = useQuery(
    'userTrainingGroups',
    () => trainingSessionsAPI.getUserTrainingGroups(),
    {
      enabled: show && !editingSession?.parent_session_id, // Only for parent sessions or new sessions
      staleTime: 5 * 60 * 1000,
    }
  );

  // Reset validation errors when form data changes
  useEffect(() => {
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  }, [formData.crew_assignments]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      setValidationErrors([]);
      await handleSubmit(e);
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.data?.detail?.errors) {
        setValidationErrors(error.response.data.detail.errors);
      } else if (error.response?.data?.detail) {
        setValidationErrors([error.response.data.detail]);
      }
    }
  };
  // Helper function to compare crew IDs consistently
  const crewIdsMatch = (id1, id2) => {
    return Number(id1) === Number(id2);
  };

  // Helper function to find crew by ID consistently
  const findCrewById = (crewId) => {
    return crews.find((c) => crewIdsMatch(c.id, crewId));
  };

  const getCurrentTrainingGroupId = () => {
    if (formData.crew_assignments.length === 0) return null;

    // Get the first crew's training group
    const firstCrewId = formData.crew_assignments[0].crew_id;
    const firstCrew = findCrewById(firstCrewId);

    return firstCrew?.training_group_id || null;
  };

  const canAddCrewFromGroup = (group) => {
    const currentGroupId = getCurrentTrainingGroupId();

    // If no crews assigned yet, can add from any group
    if (currentGroupId === null) return true;

    // Can only add crews from the same training group
    return Number(group.id) === Number(currentGroupId);
  };

  const addAllCrewsFromGroup = (group) => {
    const newCrewAssignments = [...formData.crew_assignments];
    let addedCount = 0;

    (group.crews || []).forEach((crew) => {
      // Check if crew is already assigned
      const isAlreadyAssigned = newCrewAssignments.some((assignment) =>
        crewIdsMatch(assignment.crew_id, crew.id)
      );
      // Check if crew is scheduled in child sessions (for parent sessions)
      const isScheduledInChild = editingSession && isCrewScheduledInChild(crew.id);

      // Only add if not already assigned and not scheduled in child sessions
      if (!isAlreadyAssigned && !isScheduledInChild) {
        newCrewAssignments.push({ crew_id: crew.id.toString() });
        addedCount++;
      }
    });

    if (addedCount > 0) {
      setFormData({ ...formData, crew_assignments: newCrewAssignments });
    }
  };

  const getScheduledCrewIds = () => {
    if (!editingSession?.childSessions) return new Set();

    const scheduledIds = new Set();
    editingSession.childSessions.forEach((child) => {
      child.crew_assignments?.forEach((assignment) => {
        scheduledIds.add(assignment.crew_id);
      });
    });
    return scheduledIds;
  };

  const isCrewScheduledInChild = (crewId) => {
    const scheduledIds = getScheduledCrewIds();
    return scheduledIds.has(parseInt(crewId));
  };

  if (!show) return null;

  const handleQuickDateSelect = (daysToAdd) => {
    const newDate = new Date();
    newDate.setDate(newDate.getDate() + daysToAdd);
    setFormData({ ...formData, date: newDate.toISOString().split('T')[0] });
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-600 bg-opacity-75 p-2 md:p-4"
      onClick={handleClose}
    >
      <div
        className="max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white md:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-4 py-4 md:px-6">
          <h3 className="text-lg font-medium text-gray-900">
            {editingSession
              ? editingSession.parent_session_id
                ? 'Edit Session Time'
                : 'Edit Session'
              : 'Create Unscheduled Session'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {editingSession && editingSession.parent_session_id
              ? 'Update the start time and coach assignments for this scheduled session.'
              : editingSession
                ? 'Edit session details. Changes to title and description will apply to all related sessions.'
                : 'Create a training session that can be scheduled later with specific crews and times.'}
          </p>
        </div>

        <form onSubmit={handleFormSubmit} className="space-y-6 p-4 md:p-6">
          {editingSession && editingSession.parent_session_id ? (
            <div className="space-y-4">
              <div>
                <label className="form-label">Start Time *</label>
                <input
                  type="time"
                  required
                  className="form-input"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                />
                <p className="mt-2 text-sm text-gray-500">
                  End time:{' '}
                  {formData.start_time && formData.duration
                    ? calculateEndTime(formData.start_time, parseInt(formData.duration))
                    : 'Not calculated'}
                </p>
              </div>

              <div>
                <label className="form-label">Assigned coaches</label>
                <div className="max-h-52 space-y-2 overflow-y-auto rounded-md border border-gray-200 bg-white p-3">
                  {coaches.length === 0 ? (
                    <p className="text-sm text-gray-500">No coaches available.</p>
                  ) : (
                    [...coaches]
                      .sort((a, b) =>
                        `${a.last_name || ''} ${a.first_name || ''}`.localeCompare(
                          `${b.last_name || ''} ${b.first_name || ''}`
                        )
                      )
                      .map((coach) => {
                        const coachId = coach.id;
                        const selected = (formData.selected_coach_ids || []).some(
                          (id) => Number(id) === Number(coachId)
                        );
                        return (
                          <label
                            key={coachId}
                            className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                              checked={selected}
                              onChange={(e) => {
                                const ids = formData.selected_coach_ids || [];
                                const next = e.target.checked
                                  ? [...ids, coachId]
                                  : ids.filter((x) => Number(x) !== Number(coachId));
                                setFormData({ ...formData, selected_coach_ids: next });
                              }}
                            />
                            <span className="text-sm text-gray-800">
                              {coach.first_name} {coach.last_name}
                            </span>
                          </label>
                        );
                      })
                  )}
                </div>
              </div>

              <div className="space-y-2 rounded-lg bg-gray-50 p-4">
                <div className="text-sm">
                  <span className="font-medium">Session:</span> {formData.title}
                </div>
                <div className="text-sm">
                  <span className="font-medium">Duration:</span> {formData.duration} minutes
                </div>
                <div className="text-sm">
                  <span className="font-medium">Crews:</span>{' '}
                  {formatCrewNames(formData.crew_assignments)}
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-secondary w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateSessionMutation.isLoading}
                  className="btn-primary w-full sm:w-auto"
                >
                  {updateSessionMutation.isLoading ? 'Updating...' : 'Update Time'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {editingSession &&
                editingSession.childSessions &&
                editingSession.childSessions.length > 0 && (
                  <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-amber-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-amber-800">
                          Editing Sessions with Scheduled Times
                        </h3>
                        <div className="mt-2 text-sm text-amber-700">
                          <p>
                            This session has {editingSession.childSessions.length} scheduled time
                            slot{editingSession.childSessions.length > 1 ? 's' : ''}. Date and
                            duration cannot be changed to avoid conflicts with existing schedules.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="form-label">Session Name</label>
                  <input
                    type="text"
                    required
                    className="form-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Water Training - June 11"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-input"
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description of the training session"
                  />
                </div>

                <div>
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    required
                    className={`form-input ${editingSession && editingSession.childSessions && editingSession.childSessions.length > 0 ? 'cursor-not-allowed bg-gray-100' : ''}`}
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    disabled={
                      editingSession &&
                      editingSession.childSessions &&
                      editingSession.childSessions.length > 0
                    }
                  />
                  <div className="mt-3">
                    <p className="mb-2 text-xs text-gray-600">Quick date select:</p>
                    {editingSession &&
                    editingSession.childSessions &&
                    editingSession.childSessions.length > 0 ? (
                      <p className="text-xs italic text-gray-500">
                        Date cannot be changed for sessions with scheduled child sessions
                      </p>
                    ) : (
                      <div className="space-y-1">
                        <div className="grid grid-cols-7 gap-1 text-xs">
                          {Array.from({ length: 7 }, (_, i) => {
                            const date = new Date();
                            date.setDate(date.getDate() + i);
                            // Get first letter of weekday, e.g. 'M', 'T', etc.
                            const dayLetter = date
                              .toLocaleDateString(undefined, { weekday: 'short' })
                              .charAt(0);
                            return (
                              <span
                                key={`day-indicator-${i}`}
                                className="block text-center font-semibold text-gray-400"
                              >
                                {dayLetter}
                              </span>
                            );
                          })}
                          {Array.from({ length: 28 }, (_, i) => {
                            const date = new Date();
                            date.setDate(date.getDate() + i);
                            return (
                              <button
                                key={i}
                                type="button"
                                onClick={() => handleQuickDateSelect(i)}
                                className={`rounded px-1 py-1 ${formData.date === date.toISOString().split('T')[0] ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                              >
                                {`${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}`}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="form-label">Duration (minutes) *</label>
                  <select
                    className={`form-input ${editingSession && editingSession.childSessions && editingSession.childSessions.length > 0 ? 'cursor-not-allowed bg-gray-100' : ''}`}
                    value={formData.duration}
                    required
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    disabled={
                      editingSession &&
                      editingSession.childSessions &&
                      editingSession.childSessions.length > 0
                    }
                  >
                    <option value="60">60 minutes (1 hour)</option>
                    <option value="90">90 minutes (1.5 hours)</option>
                    <option value="120">120 minutes (2 hours)</option>
                    <option value="150">150 minutes (2.5 hours)</option>
                    <option value="180">180 minutes (3 hours)</option>
                  </select>
                  <div className="mt-2">
                    <p className="mb-2 text-xs text-gray-600">Quick select:</p>
                    {editingSession &&
                    editingSession.childSessions &&
                    editingSession.childSessions.length > 0 ? (
                      <p className="text-xs italic text-gray-500">
                        Duration cannot be changed for sessions with scheduled child sessions
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2 text-xs">
                        {[
                          { value: '60', label: '1h' },
                          { value: '90', label: '1.5h' },
                          { value: '120', label: '2h' },
                          { value: '150', label: '2.5h' },
                          { value: '180', label: '3h' },
                        ].map((duration) => (
                          <button
                            key={duration.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, duration: duration.value })}
                            className={`rounded px-2 py-1 ${formData.duration === duration.value ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                          >
                            {duration.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    <label className="form-label">Location</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., Bosbaan, Amsterdam"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Crew Assignment</h4>
                    <p className="mt-1 text-sm text-gray-600">
                      Select crews that need this training session. All crews must belong to the
                      same training group.{' '}
                      <b>
                        You will be able to schedule this session for different crews at different
                        times!
                      </b>
                    </p>
                  </div>
                </div>

                {/* Validation Errors */}
                {validationErrors.length > 0 && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
                    <div className="mb-2 flex items-center">
                      <svg
                        className="mr-2 h-5 w-5 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <h5 className="text-sm font-medium text-red-800">Validation Errors</h5>
                    </div>
                    <ul className="space-y-1 text-sm text-red-700">
                      {validationErrors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Training Groups Section - Only show for parent sessions (not child sessions) */}
                {!editingSession?.parent_session_id &&
                  !loadingTrainingGroups &&
                  userTrainingGroups.length > 0 && (
                    <div className="mb-4">
                      <div className="mb-2 flex items-center justify-between">
                        <label className="form-label text-sm">
                          Add crews from your Training Groups
                        </label>
                        {getCurrentTrainingGroupId() && (
                          <div className="mt-1 text-xs text-blue-600">
                            Only groups with crews from the same training group are enabled
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowTrainingGroups(!showTrainingGroups)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {showTrainingGroups ? 'Hide' : 'Show'} Training Groups
                        </button>
                      </div>

                      {showTrainingGroups && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="space-y-3">
                            {userTrainingGroups.map((group) => (
                              <div
                                key={group.id}
                                className="rounded border border-gray-200 bg-white p-3"
                              >
                                <div className="mb-2 flex items-center justify-between">
                                  <div>
                                    <h5 className="text-sm font-medium text-gray-900">
                                      {group.name}
                                    </h5>
                                    <p className="text-xs text-gray-500">
                                      {group.crews?.length || 0} crews
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => addAllCrewsFromGroup(group)}
                                    className="rounded bg-blue-600 px-3 py-1 text-xs text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                                    disabled={
                                      !canAddCrewFromGroup(group) ||
                                      group.crews?.every((crew) => {
                                        const isAlreadyAssigned = formData.crew_assignments.some(
                                          (assignment) => crewIdsMatch(assignment.crew_id, crew.id)
                                        );
                                        const isScheduledInChild =
                                          editingSession && isCrewScheduledInChild(crew.id);
                                        return isAlreadyAssigned || isScheduledInChild;
                                      })
                                    }
                                  >
                                    {(() => {
                                      const availableCrews =
                                        group.crews?.filter((crew) => {
                                          const isAlreadyAssigned = formData.crew_assignments.some(
                                            (assignment) =>
                                              crewIdsMatch(assignment.crew_id, crew.id)
                                          );
                                          const isScheduledInChild =
                                            editingSession && isCrewScheduledInChild(crew.id);
                                          return !isAlreadyAssigned && !isScheduledInChild;
                                        }) || [];

                                      if (availableCrews.length === 0) {
                                        return 'All Added';
                                      } else if (availableCrews.length === group.crews?.length) {
                                        return 'Add All Crews';
                                      } else {
                                        return `Add ${availableCrews.length} Crews`;
                                      }
                                    })()}
                                  </button>
                                </div>
                                <div className="space-y-1">
                                  {group.crews?.map((crew) => {
                                    const isAssigned = formData.crew_assignments.some(
                                      (assignment) => crewIdsMatch(assignment.crew_id, crew.id)
                                    );
                                    const isScheduledInChild =
                                      editingSession && isCrewScheduledInChild(crew.id);

                                    return (
                                      <div
                                        key={crew.id}
                                        className="flex items-center justify-between text-xs"
                                      >
                                        <div className="flex items-center">
                                          <span
                                            className={`${isAssigned ? 'text-green-600' : 'text-gray-600'}`}
                                          >
                                            {crew.name}
                                          </span>
                                          <span className="ml-2 text-gray-400">
                                            ({crew.boat_type})
                                          </span>
                                          {crew.is_experimental && (
                                            <span className="ml-1 rounded bg-blue-100 px-1 py-0.5 text-blue-600">
                                              EXP
                                            </span>
                                          )}
                                          {isScheduledInChild && (
                                            <span className="ml-1 rounded bg-orange-100 px-1 py-0.5 text-orange-600">
                                              Scheduled
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center">
                                          {isAssigned && (
                                            <span className="text-green-600">✓ Added</span>
                                          )}
                                          {isScheduledInChild && (
                                            <span className="ml-2 text-xs text-orange-600">
                                              ⚠ Already in a scheduled child-session
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                <div className="mb-4">
                  <label className="form-label text-sm">Add Individual Crew</label>
                  {getCurrentTrainingGroupId() && (
                    <div className="mt-1 text-xs text-blue-600">
                      Only showing crews from the same training group as the selected crew
                    </div>
                  )}
                  <select
                    className="form-input text-sm"
                    value=""
                    onChange={(e) => addCrewAssignment(e.target.value)}
                  >
                    <option value="">Select crew to add...</option>
                    {crews
                      .filter((crew) => {
                        const isAlreadyAssigned = formData.crew_assignments.some((assignment) =>
                          crewIdsMatch(assignment.crew_id, crew.id)
                        );
                        const isScheduledInChild =
                          editingSession && isCrewScheduledInChild(crew.id);
                        const currentGroupId = getCurrentTrainingGroupId();
                        const canAddFromThisGroup =
                          currentGroupId === null ||
                          Number(crew.training_group_id) === Number(currentGroupId);

                        // For parent sessions, hide crews that are already scheduled in child sessions
                        return (
                          !isAlreadyAssigned &&
                          (!editingSession?.childSessions || !isScheduledInChild) &&
                          canAddFromThisGroup
                        );
                      })
                      .map((crew) => (
                        <option key={crew.id} value={crew.id}>
                          {crew.name} ({crew.boat_type})
                          {crew.is_experimental ? ' - Experimental' : ''}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Warning for crews already scheduled in child sessions */}
                {editingSession?.childSessions && getScheduledCrewIds().size > 0 && (
                  <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-3">
                    <div className="flex items-center">
                      <svg
                        className="mr-2 h-4 w-4 text-orange-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-sm text-orange-800">
                        <strong>Note:</strong> {getScheduledCrewIds().size} crew(s) are already
                        scheduled for this session and therefor cannot be added.
                      </p>
                    </div>
                  </div>
                )}

                {formData.crew_assignments.length === 0 ? (
                  <div className="rounded-lg border-2 border-dashed border-gray-300 py-6 text-center">
                    <p className="text-sm text-gray-500">
                      No crews assigned - at least one crew must be assigned to this session
                    </p>
                    <p className="mt-2 text-xs text-gray-400">
                      Use the dropdown above or training groups to add crews to this session
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.crew_assignments.map((assignment, index) => {
                      const crew = findCrewById(assignment.crew_id);
                      const isScheduledInChild =
                        editingSession && isCrewScheduledInChild(assignment.crew_id);

                      return (
                        <div
                          key={index}
                          className={`flex items-center gap-3 rounded-lg border p-3 ${isScheduledInChild ? 'border-red-200 bg-red-50' : 'bg-gray-50'}`}
                        >
                          <div className="flex-1">
                            <label className="form-label text-sm">Crew</label>
                            <select
                              className={`form-input text-sm ${isScheduledInChild ? 'border-red-300' : ''}`}
                              value={assignment.crew_id}
                              onChange={(e) =>
                                updateCrewAssignment(index, 'crew_id', e.target.value)
                              }
                              required
                            >
                              <option value="">Select crew</option>
                              {crews.map((crew) => {
                                const wouldBeScheduledInChild =
                                  editingSession && isCrewScheduledInChild(crew.id);
                                return (
                                  <option
                                    key={crew.id}
                                    value={crew.id}
                                    disabled={wouldBeScheduledInChild}
                                  >
                                    {crew.name} ({crew.boat_type})
                                    {crew.is_experimental ? ' - Experimental' : ''}
                                    {wouldBeScheduledInChild
                                      ? ' - Already scheduled in child session'
                                      : ''}
                                  </option>
                                );
                              })}
                            </select>
                            {isScheduledInChild && (
                              <p className="mt-1 text-xs text-red-600">
                                ⚠ This crew is already scheduled in a child session and will cause
                                a validation error
                              </p>
                            )}
                          </div>

                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={() => removeCrewAssignment(index)}
                              className="btn-outline border-red-300 py-1 text-sm text-red-600 hover:bg-red-50"
                              title="Remove crew"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-secondary w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createSessionMutation.isLoading || updateSessionMutation.isLoading}
                  className="btn-primary w-full sm:w-auto"
                >
                  {createSessionMutation.isLoading || updateSessionMutation.isLoading
                    ? 'Saving...'
                    : editingSession
                      ? 'Update Session'
                      : 'Create Session'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateSessionModal;
